/**
 * Fixed-step game loop with an accumulator, render decoupled from sim.
 *
 * Why fixed step: physics + lane snap behave deterministically across
 * 60Hz / 120Hz ProMotion / dropped frames. Render runs every rAF for
 * smoothness; simulation runs in 1/60s slices.
 *
 * dt clamp prevents spiral-of-death after tab resume.
 */
export type Updater = (fixedDt: number) => void;
export type Renderer = (alpha: number, frameDt: number) => void;

export class Loop {
  private rafId = 0;
  private lastT = 0;
  private acc = 0;
  private running = false;

  readonly fixedDt: number;
  private readonly maxFrameDt: number;

  constructor(
    private update: Updater,
    private render: Renderer,
    fixedHz = 60,
  ) {
    this.fixedDt = 1 / fixedHz;
    // Clamp wall-clock dt so a long pause doesn't blow up sim.
    this.maxFrameDt = 1 / 4;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastT = performance.now();
    this.acc = 0;
    const tick = (now: number) => {
      if (!this.running) return;
      let frameDt = (now - this.lastT) / 1000;
      this.lastT = now;
      if (frameDt > this.maxFrameDt) frameDt = this.maxFrameDt;
      this.acc += frameDt;
      // Run 0..N fixed steps. Cap iterations so heavy frames don't snowball.
      let steps = 0;
      while (this.acc >= this.fixedDt && steps < 5) {
        this.update(this.fixedDt);
        this.acc -= this.fixedDt;
        steps++;
      }
      const alpha = this.acc / this.fixedDt;
      this.render(alpha, frameDt);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  isRunning(): boolean {
    return this.running;
  }
}
